/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { isTypedArray, isObject, isUndefinedOrNull } from './types.js';

export function deepClone<T>(obj: T): T {
	if (!obj || typeof obj !== 'object') {
		return obj;
	}
	if (obj instanceof RegExp) {
		return obj;
	}
	const result: any = Array.isArray(obj) ? [] : {};
	Object.entries(obj).forEach(([key, value]) => {
		result[key] = value && typeof value === 'object' ? deepClone(value) : value;
	});
	return result;
}

export function deepFreeze<T>(obj: T): T {
	if (!obj || typeof obj !== 'object') {
		return obj;
	}
	const stack: any[] = [obj];
	while (stack.length > 0) {
		const obj = stack.shift();
		Object.freeze(obj);
		for (const key in obj) {
			if (_hasOwnProperty.call(obj, key)) {
				const prop = obj[key];
				if (typeof prop === 'object' && !Object.isFrozen(prop) && !isTypedArray(prop)) {
					stack.push(prop);
				}
			}
		}
	}
	return obj;
}

const _hasOwnProperty = Object.prototype.hasOwnProperty;


export function cloneAndChange(obj: any, changer: (orig: any) => any): any {
	return _cloneAndChange(obj, changer, new Set());
}

function _cloneAndChange(obj: any, changer: (orig: any) => any, seen: Set<any>): any {
	if (isUndefinedOrNull(obj)) {
		return obj;
	}

	const changed = changer(obj);
	if (typeof changed !== 'undefined') {
		return changed;
	}

	if (Array.isArray(obj)) {
		const r1: any[] = [];
		for (const e of obj) {
			r1.push(_cloneAndChange(e, changer, seen));
		}
		return r1;
	}

	if (isObject(obj)) {
		if (seen.has(obj)) {
			throw new Error('Cannot clone recursive data-structure');
		}
		seen.add(obj);
		const r2: Record<string, unknown> = {};
		for (const i2 in obj) {
			if (_hasOwnProperty.call(obj, i2)) {
				r2[i2] = _cloneAndChange(obj[i2], changer, seen);
			}
		}
		seen.delete(obj);
		return r2;
	}

	return obj;
}

/**
 * Copies all properties of source into destination. The optional parameter "overwrite" allows to control
 * if existing properties on the destination should be overwritten or not. Defaults to true (overwrite).
 */
export function mixin(destination: any, source: any, overwrite: boolean = true): any {
	if (!isObject(destination)) {
		return source;
	}

	if (isObject(source)) {
		Object.keys(source).forEach(key => {
			if (key in destination) {
				if (overwrite) {
					if (isObject(destination[key]) && isObject(source[key])) {
						mixin(destination[key], source[key], overwrite);
					} else {
						destination[key] = source[key];
					}
				}
			} else {
				destination[key] = source[key];
			}
		});
	}
	return destination;
}

export function equals(one: any, other: any): boolean {
	if (one === other) {
		return true;
	}
	if (one === null || one === undefined || other === null || other === undefined) {
		return false;
	}
	if (typeof one !== typeof other) {
		return false;
	}
	if (typeof one !== 'object') {
		return false;
	}
	if ((Array.isArray(one)) !== (Array.isArray(other))) {
		return false;
	}

	let i: number;
	let key: string;

	if (Array.isArray(one)) {
		if (one.length !== other.length) {
			return false;
		}
		for (i = 0; i < one.length; i++) {
			if (!equals(one[i], other[i])) {
				return false;
			}
		}
	} else {
		const oneKeys: string[] = [];

		for (key in one) {
			oneKeys.push(key);
		}
		oneKeys.sort();
		const otherKeys: string[] = [];
		for (key in other) {
			otherKeys.push(key);
		}
		otherKeys.sort();
		if (!equals(oneKeys, otherKeys)) {
			return false;
		}
		for (i = 0; i < oneKeys.length; i++) {
			if (!equals(one[oneKeys[i]], other[oneKeys[i]])) {
				return false;
			}
		}
	}
	return true;
}

/**
 * Calls `JSON.Stringify` with a replacer to break apart any circular references.
 * This prevents `JSON`.stringify` from throwing the exception
 *  "Uncaught TypeError: Converting circular structure to JSON"
 */
export function safeStringify(obj: any, space?: string | number, replacer?: (this: any, key: string, value: any) => any): string {
	// A cycle is a reference to an ANCESTOR — that is the only thing JSON cannot
	// represent. Marking every REPEATED reference instead (a global "seen" set)
	// silently blanked shared objects: a glTF material points aoMap, roughnessMap
	// and metalnessMap at one packed texture, and the second and third came back
	// as "[Circular]" with no value for anyone to read.
	//
	// `this` is the object holding the current key, so popping until it is on top
	// leaves exactly the path from the root to here.
	const ancestors: any[] = [];
	return JSON.stringify(obj, function (this: any, key, value) {
		if (isObject(value) || Array.isArray(value)) {
			while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
				ancestors.pop();
			}
			if (ancestors.includes(value)) {
				return '[Circular]';
			}
			ancestors.push(value);
		}
		if (typeof value === 'bigint') {
			return `[BigInt ${value.toString()}]`;
		}
		// Collections have no own enumerable properties, so JSON.stringify renders
		// every one of them as `{}` — indistinguishable from a genuinely empty
		// object. That is actively misleading when inspecting live engine state: a
		// three NodeBuilder's `nodesData` WeakMap holding hundreds of entries reads
		// as "empty", and a reader concludes the cache was never populated.
		// Map/Set can report their size; Weak* deliberately cannot (their contents
		// are unenumerable by design, since exposing them would leak GC timing), so
		// they report only their kind — which is still strictly more than `{}`.
		// NOT `instanceof`: these objects routinely come from the INSPECTED PAGE's
		// realm (a three NodeBuilder is the page's, not ours), where `instanceof`
		// is false for the very collections we are trying to describe. The tag is
		// realm-independent.
		switch (Object.prototype.toString.call(value)) {
			case '[object Map]':
				return `[Map size ${(value as Map<unknown, unknown>).size}]`;
			case '[object Set]':
				return `[Set size ${(value as Set<unknown>).size}]`;
			case '[object WeakMap]':
				return '[WeakMap]';
			case '[object WeakSet]':
				return '[WeakSet]';
		}
		if (replacer) {
			return replacer.call(this, key, value);
		}
		return value;
	}, space);
}

/**
 * Replacer for CLIPBOARD/display-bound {@link safeStringify} — a readability
 * cut for humans/AI, never a data-transfer format. "Copy as JSON" of a
 * geometry or material otherwise dumps entire buffers no paste target wants:
 * - long NUMBER arrays (plain or typed — attribute buffers, mip data) are cut
 *   to the first `maxEntries` values plus a self-describing marker,
 * - data-url strings (texture images serialized by three's toJSON) are
 *   OMITTED entirely — base64 pixels have zero paste value at any length,
 * - other very long strings keep a readable head plus a marker.
 * Structural (object) arrays, short arrays and ordinary strings pass through.
 */
export function truncateLargeValuesReplacer(maxEntries = 10_000): (this: any, key: string, value: any) => any {
	const MAX_STRING_LENGTH = 4_096;
	const STRING_HEAD = 1_024;
	return function (_key, value) {
		// typed arrays JSON-stringify as index-keyed OBJECTS — normalize first
		if (ArrayBuffer.isView(value) && !(value instanceof DataView)) {
			value = Array.from(value as unknown as ArrayLike<number>);
		}
		if (Array.isArray(value) && value.length > maxEntries && value.every(entry => typeof entry === "number")) {
			return [...value.slice(0, maxEntries), `[… ${value.length - maxEntries} more entries truncated]`];
		}
		if (typeof value === "string" && value.length > MAX_STRING_LENGTH) {
			const dataUrl = /^data:([^;,]+)/.exec(value);
			if (dataUrl) {
				// base64 ≈ 3/4 payload bytes per character
				const bytes = Math.round(value.length * 0.75);
				return `[${dataUrl[0]} omitted — ~${(bytes / (1024 * 1024)).toFixed(1)} MB]`;
			}
			return `${value.substring(0, STRING_HEAD)} [… ${value.length - STRING_HEAD} more characters truncated]`;
		}
		return value;
	};
}

type obj = { [key: string]: any };
/**
 * Returns an object that has keys for each value that is different in the base object. Keys
 * that do not exist in the target but in the base object are not considered.
 *
 * Note: This is not a deep-diffing method, so the values are strictly taken into the resulting
 * object if they differ.
 *
 * @param base the object to diff against
 * @param obj the object to use for diffing
 */
export function distinct(base: obj, target: obj): obj {
	const result = Object.create(null);

	if (!base || !target) {
		return result;
	}

	const targetKeys = Object.keys(target);
	targetKeys.forEach(k => {
		const baseValue = base[k];
		const targetValue = target[k];

		if (!equals(baseValue, targetValue)) {
			result[k] = targetValue;
		}
	});

	return result;
}

export function getCaseInsensitive(target: obj, key: string): unknown {
	const lowercaseKey = key.toLowerCase();
	const equivalentKey = Object.keys(target).find(k => k.toLowerCase() === lowercaseKey);
	return equivalentKey ? target[equivalentKey] : target[key];
}

export function filter(obj: obj, predicate: (key: string, value: any) => boolean): obj {
	const result = Object.create(null);
	for (const [key, value] of Object.entries(obj)) {
		if (predicate(key, value)) {
			result[key] = value;
		}
	}
	return result;
}

export function mapValues<T extends {}, R>(obj: T, fn: (value: T[keyof T], key: string) => R): { [K in keyof T]: R } {
	const result: { [key: string]: R } = {};
	for (const [key, value] of Object.entries(obj)) {
		result[key] = fn(<T[keyof T]>value, key);
	}
	return result as { [K in keyof T]: R };
}
